import type { RecoilState } from 'recoil';
import { atom, useRecoilValue, useSetRecoilState } from 'recoil';
import { createContext, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { Stack } from 'common/ui/Layout';
import { css } from 'styled-components';
import type { FlattenSimpleInterpolation } from 'styled-components';
import { broadcastChannelSynchronizerEffect } from 'utils/recoilEffects';
import {
  HANGING_PROTOCOL_SIDEBAR_ID,
  useShouldShowHangingProtocolWidget,
} from './StudiesBar/HangingProtocols/HangingProtocolWidget';

export type SidebarState = {
  isOpen: boolean;
  activeSidebarId?: string;
  showOverlay: boolean;
};

type ContextValue = {
  current: HTMLDivElement | null;
};

export const SIDEBAR_MAX_PX_WIDTH = 373;

export const SIDEBAR_CONTAINER_ID = 'sidebar-container-portal';

const DEFAULT_SIDEBAR_STATE: SidebarState = {
  activeSidebarId: 'none',
  isOpen: false,
  showOverlay: false,
};

export const sidebarState: RecoilState<SidebarState> = atom<SidebarState>({
  key: 'portalSidebarState',
  default: DEFAULT_SIDEBAR_STATE,
  effects: [broadcastChannelSynchronizerEffect()],
});

export const useSetSidebarState = (): ((state: SidebarState) => void) =>
  useSetRecoilState(sidebarState);

export const useSidebarState = (): SidebarState => useRecoilValue(sidebarState);

export const useCloseSidebar = (): (() => void) => {
  const setSidebarState = useSetSidebarState();
  return () => setSidebarState(DEFAULT_SIDEBAR_STATE);
};

export const SidebarContext: React.Context<ContextValue> = createContext<ContextValue>({
  current: null,
});

export const SidebarContainer = ({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement => {
  return (
    <Stack stretchX stretchY>
      <SidebarParent />
      <SidebarChildren>{children}</SidebarChildren>
    </Stack>
  );
};

export const Sidebar = ({
  children,
  isOpen,
  activeSidebarId,
  showOverlay,
}: {
  children: React.ReactNode;
} & SidebarState): React.ReactElement | null => {
  const sidebarRef = useRef<HTMLElement | null>(document.getElementById(SIDEBAR_CONTAINER_ID));
  const sidebarState = useSidebarState();
  const setSidebarState = useSetSidebarState();

  useEffect(() => {
    if (!sidebarState.isOpen && !isOpen) {
      return;
    }
    if (
      sidebarState.isOpen !== isOpen ||
      sidebarState.activeSidebarId !== activeSidebarId ||
      sidebarState.showOverlay !== showOverlay
    ) {
      setSidebarState({
        isOpen,
        activeSidebarId: isOpen ? activeSidebarId : DEFAULT_SIDEBAR_STATE.activeSidebarId,
        showOverlay: showOverlay && isOpen,
      });
    }
  }, [
    activeSidebarId,
    isOpen,
    setSidebarState,
    showOverlay,
    sidebarRef,
    sidebarState.activeSidebarId,
    sidebarState.isOpen,
    sidebarState.showOverlay,
  ]);

  // Ensure we alyways have a sidebar container
  const target = sidebarRef.current ?? document.getElementById(SIDEBAR_CONTAINER_ID);

  if (!isOpen || !target) {
    return null;
  }

  return createPortal(children, target, activeSidebarId);
};

const siblingCss = css`
  z-index: 40;
`;

const enabledOverlayContainerCss = css`
  position: relative;
  pointer-events: none;
`;

export const SidebarOverlay = ({
  children,
  css: cssProp,
}: {
  children: React.ReactNode;
  css?: FlattenSimpleInterpolation;
}): React.ReactElement => {
  const { showOverlay } = useSidebarState();

  return (
    <div css={showOverlay ? enabledOverlayContainerCss : undefined}>
      <div css={showOverlay ? siblingCss : undefined}>{children}</div>
      <div
        css={css`
          pointer-events: none;
          position: absolute;
          background-color: rgba(0, 0, 0, 0.5);
          width: ${showOverlay ? 100 : 0}%;
          height: 100%;
          top: 0;
          z-index: 50;
          overflow: hidden;
          transition: width 0.3s ease-in;
          ${cssProp}
        `}
      />
    </div>
  );
};

const SidebarParent = (): React.ReactElement => {
  const showSidebar = useShowSidebar();

  return (
    <Stack className="SidebarContainer">
      <div
        data-testid={SIDEBAR_CONTAINER_ID}
        id={SIDEBAR_CONTAINER_ID}
        css={css`
          width: ${showSidebar ? SIDEBAR_MAX_PX_WIDTH : 0}px;
          transition: width 0.3s ease-in;
          overflow: hidden;
        `}
      />
    </Stack>
  );
};

const SidebarChildren = ({ children }: { children: React.ReactNode }): React.ReactElement => {
  const showSidebar = useShowSidebar();

  return (
    <Stack
      vertical
      stretchX
      stretchY
      css={css`
        width: ${showSidebar ? `calc(100% - ${SIDEBAR_MAX_PX_WIDTH}px)` : '100%'};
      `}
    >
      {children}
    </Stack>
  );
};

function useShowSidebar() {
  const { isOpen, activeSidebarId } = useSidebarState();
  const showHangingProtocolWidget = useShouldShowHangingProtocolWidget();

  return (
    isOpen && (!(activeSidebarId === HANGING_PROTOCOL_SIDEBAR_ID) || showHangingProtocolWidget)
  );
}
